import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { toNational } from '@/utilities/formatPhoneNumber';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { genAttributes } from '@onward-delivery/core';
import { carrierDisplayName } from '@/utilities/carrierAlias';
import { routeDisplayName } from '@/utilities/routeNames';

/**
 * Prepares manifest data for PDF generation
 * Transforms route/order/stop data into structured format for ManifestPDF
 */
export const prepareManifestData = (route, allDrivers, { partnerCarriersById } = {}) => {
    const stops = FTLStopHelpers.getStopSequence(route.stopsByRouteId);

    // Get first and last stop for departure/destination
    const departureStop = stops[0];
    const destinationStop = stops[stops.length - 1];

    // Get driver info
    const driver = allDrivers?.find((d) => d.teammate_id === route.driver_id) || {};

    // Get timezone from first order
    const firstOrder = route.orders?.[0]?.order;
    let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (firstOrder) {
        const { zip } = genAttributes(firstOrder);
        const lookupTz = zipcode_to_timezone.lookup(firstOrder[zip]);
        if (lookupTz) {
            timezone = lookupTz;
        }
    }

    // Build orders array with items
    const orders = [];

    // Iterate through stops and get orders at each stop
    const processedOrderIds = new Set();

    stops.forEach((stop) => {
        // Skip pickup-only stops without returns
        if (stop.type === 'PICKUP' && !stop.returns?.length) return;

        // Get all orders at this stop
        const ordersAtStop =
            route.orders
                ?.filter((mapping) => stop.orders?.includes?.(mapping.order_id))
                .map((mapping) => mapping.order) || [];

        ordersAtStop.forEach((order) => {
            if (!order || processedOrderIds.has(order.order_id)) return;
            processedOrderIds.add(order.order_id);

            // Get items for this order
            const items = (order.itemsByOrderId || []).map((item) => ({
                item_id: item.po_number || '',
                sku: formatSKU(item.sku || ''),
                description: item.description || '',
                barcode: formatBarcode(item.barcode || ''),
                weight: formatWeight(item),
                cubes: formatCubes(item),
            }));

            orders.push({
                stop_number: stop.ordering + 1, // 1-indexed stop number
                order_number: order.order_number,
                po_number: order.po_number || '',
                customer_name: order.dropoff_name || '',
                customer_phone: order.dropoff_phone ? toNational(order.dropoff_phone) : '',
                customer_address: formatAddress(order.dropoff_address, order.dropoff_location_info),
                customer_address1: order.dropoff_street || '',
                customer_address2: order.dropoff_location_info || '',
                customer_city: order.dropoff_city || '',
                customer_state: order.dropoff_state || '',
                customer_zip: order.dropoff_zip || '',
                time_frame: formatTimeFrame(stop.del_window_start, stop.del_window_end),
                items: items,
            });
        });
    });
    return {
        route_number: route.route_number,
        route_alias: route.route_alias || null,
        // The name the Plan page shows.
        route_name: routeDisplayName(route),
        issue_date: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }),
        warehouse_name: 'Warehouse 1', // Placeholder - can be configured later
        departure_address: formatStopAddress(departureStop),
        destination_address: formatStopAddress(destinationStop),
        // The resource itself, never the route's alias: "Resource: North loop" names the route twice.
        resource: route.resource?.name || '',
        carrier: carrierDisplayName(route?.route_carrier, partnerCarriersById) || '',
        assignee: driver.username || 'Unassigned',
        orders: orders,
        timezone: timezone,
    };
};

/**
 * Format weight for display
 */
const formatWeight = (item) => {
    if (item.total_weight) {
        if (Array.isArray(item.total_weight)) {
            return item.total_weight[1]?.toFixed(0) || '--';
        }
        return Math.round(item.total_weight);
    }
    return '--';
};

/**
 * Format cubes for display
 */
const formatCubes = (item) => {
    if (item.total_cubes) {
        if (Array.isArray(item.total_cubes)) {
            return item.total_cubes[1]?.toFixed(0) || '--';
        }
        return Math.round(item.total_cubes);
    }
    return '--';
};

/**
 * Format barcode with line breaks for wrapping
 */
const formatBarcode = (barcode) => {
    if (!barcode) return '';
    // Break barcode into chunks of 10 characters with newlines
    const chunks = [];
    for (let i = 0; i < barcode.length; i += 10) {
        chunks.push(barcode.substring(i, i + 10));
    }
    return chunks.join('\n');
};

/**
 * Format SKU with line breaks for wrapping
 */
const formatSKU = (sku) => {
    if (!sku || sku.length <= 8) return sku;
    // Break SKU into chunks of 8 characters with newlines for longer SKUs
    const chunks = [];
    for (let i = 0; i < sku.length; i += 8) {
        chunks.push(sku.substring(i, i + 8));
    }
    return chunks.join('\n');
};

/**
 * Format delivery address
 */
const formatAddress = (address, locationInfo) => {
    const parts = [];
    if (address) parts.push(address);
    if (locationInfo) parts.push(locationInfo);
    return parts.join(' - ');
};

/**
 * Format stop address for header
 */
const formatStopAddress = (stop) => {
    if (!stop) return '';
    return stop.address || '';
};

/**
 * Format time frame from window start/end
 */
const formatTimeFrame = (startTime, endTime) => {
    if (!startTime || !endTime) return '';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const timeFormat = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
    return `${timeFormat.format(start)} - ${timeFormat.format(end)}`;
};
